import React from 'react';
import { Box, Typography, Tooltip as MuiTooltip, Paper } from '@mui/material';

interface CorrelationHeatmapProps {
  symbols: string[];
  symbolNames: { [symbol: string]: string };
  matrix: number[][];
  onCellClick?: (row: string, col: string, value: number) => void;
}

// Color scale: -1 (red) -> 0 (amber/orange) -> 1 (green)
const getCorrelationColor = (value: number): string => {
  if (value >= 0) {
    // Positive: amber (255,160,0) to green (76,175,80)
    const t = Math.min(value, 1);
    const red = Math.round(255 - (255 - 76) * t);
    const green = Math.round(160 + (175 - 160) * t);
    const blue = Math.round(0 + 80 * t);
    return `rgb(${red}, ${green}, ${blue})`;
  } else {
    // Negative: amber (255,160,0) to red (220,50,50)
    const t = Math.min(Math.abs(value), 1);
    const red = Math.round(255 - (255 - 220) * t);
    const green = Math.round(160 - (160 - 50) * t);
    const blue = Math.round(0 + 50 * t);
    return `rgb(${red}, ${green}, ${blue})`;
  }
};

const getTextColor = (value: number): string => {
  // All cells are dark enough for white text with this color scheme
  return '#fff';
};

const CorrelationHeatmap: React.FC<CorrelationHeatmapProps> = ({
  symbols,
  symbolNames,
  matrix,
  onCellClick,
}) => {
  const n = symbols.length;

  if (n === 0 || matrix.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No correlation data available</Typography>
      </Paper>
    );
  }

  // Dynamic sizing based on number of symbols
  const cellSize = n <= 10 ? 60 : n <= 15 ? 50 : 40;
  const labelWidth = 80;
  const labelHeight = 80;

  const width = labelWidth + n * cellSize + 20;
  const height = labelHeight + n * cellSize + 20;

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxWidth: '100%' }}>
        <svg width={width} height={height} style={{ display: 'block' }}>
          {/* Column headers (rotated) */}
          {symbols.map((symbol, i) => (
            <g key={`col-${symbol}`}>
              <text
                x={labelWidth + i * cellSize + cellSize / 2}
                y={labelHeight - 8}
                textAnchor="start"
                transform={`rotate(-45, ${labelWidth + i * cellSize + cellSize / 2}, ${labelHeight - 8})`}
                fontSize={cellSize > 45 ? 11 : 9}
                fontWeight="500"
                fill="#e2e8f0"
              >
                {symbol}
              </text>
            </g>
          ))}

          {/* Row headers and cells */}
          {symbols.map((rowSymbol, i) => (
            <g key={`row-${rowSymbol}`}>
              {/* Row label */}
              <text
                x={labelWidth - 8}
                y={labelHeight + i * cellSize + cellSize / 2 + 4}
                textAnchor="end"
                fontSize={cellSize > 45 ? 11 : 9}
                fontWeight="500"
                fill="#e2e8f0"
              >
                {rowSymbol}
              </text>

              {/* Cells */}
              {symbols.map((colSymbol, j) => {
                const value = matrix[i][j];
                const displayValue = value.toFixed(2);
                const color = getCorrelationColor(value);
                const textColor = getTextColor(value);

                return (
                  <MuiTooltip
                    key={`cell-${rowSymbol}-${colSymbol}`}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {symbolNames[rowSymbol] || rowSymbol}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                          vs {symbolNames[colSymbol] || colSymbol}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: value >= 0 ? '#4caf50' : '#f44336',
                            fontWeight: 'bold',
                          }}
                        >
                          Correlation: {displayValue}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <g
                      style={{ cursor: onCellClick ? 'pointer' : 'default' }}
                      onClick={() => onCellClick?.(rowSymbol, colSymbol, value)}
                    >
                      <rect
                        x={labelWidth + j * cellSize + 1}
                        y={labelHeight + i * cellSize + 1}
                        width={cellSize - 2}
                        height={cellSize - 2}
                        fill={color}
                        stroke="#1e293b"
                        strokeWidth={1}
                        rx={2}
                      />
                      <text
                        x={labelWidth + j * cellSize + cellSize / 2}
                        y={labelHeight + i * cellSize + cellSize / 2 + 4}
                        textAnchor="middle"
                        fontSize={cellSize > 45 ? 10 : 8}
                        fill={textColor}
                        fontWeight={i === j ? 'bold' : 'normal'}
                      >
                        {displayValue}
                      </text>
                    </g>
                  </MuiTooltip>
                );
              })}
            </g>
          ))}
        </svg>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 3,
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              bgcolor: 'rgb(220, 50, 50)',
              borderRadius: 0.5,
              border: '1px solid #334155',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            -1.0 (Strong Negative)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              bgcolor: 'rgb(255, 160, 0)',
              border: '1px solid #334155',
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            0 (No Correlation)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              bgcolor: 'rgb(76, 175, 80)',
              borderRadius: 0.5,
              border: '1px solid #334155',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            +1.0 (Strong Positive)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CorrelationHeatmap;
